import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ChoiceStep } from './ChoiceStep';
import {
    confirmEmployeeProfileSelection,
    copyProfileFromEmployee,
} from './confirmEmployeeProfileSelection';
import { fullName } from './helpers';
import { ProfileSelector, type ProfileSelectorResult } from './ProfileSelector';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';

import { useSetEmployeeCompetencyOverlay } from '@/api/competency-services/employee-competency';
import { useAssignProfile } from '@/api/competency-services/profile-assignment';
import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';

export type EmployeeCompetencyAssignmentFlowProps = {
    employee: RosterEmployee;
    copyCandidates: RosterEmployee[];
    /** Start on the selector when editing an already-assigned employee. */
    initialMode?: 'choice' | 'selector';
    onDone: () => void;
    onCancel: () => void;
    onCreateGuided: (employeeId: string, employeeName: string) => void;
    /** When false, the "Create profile" action is disabled with an explanatory tooltip. */
    canManageCompetencies: boolean;
};

/**
 * Single-employee assignment orchestrator: choice (select existing / create guided /
 * copy from) ↔ profile selector (remove/restore/add, then confirm).
 */
export const EmployeeCompetencyAssignmentFlow = ({
    employee,
    copyCandidates,
    initialMode = isValidProfileId(employee.assignedProfileId) ? 'selector' : 'choice',
    onDone,
    onCancel,
    onCreateGuided,
    canManageCompetencies,
}: EmployeeCompetencyAssignmentFlowProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const queryClient = useQueryClient();
    const { mutateAsync: assignProfile } = useAssignProfile();
    const { mutateAsync: setOverlay } = useSetEmployeeCompetencyOverlay();

    const [step, setStep] = useState<'choice' | 'selector'>(initialMode);
    const [cameFromChoice, setCameFromChoice] = useState(initialMode === 'choice');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async (result: ProfileSelectorResult) => {
        setIsSubmitting(true);
        try {
            await confirmEmployeeProfileSelection({
                employee,
                result,
                assignProfile,
                setOverlay,
                queryClient,
                t,
            });
            onDone();
        } catch (error) {
            handleError({ error });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCopyFrom = async (sourceEmployeeId: string) => {
        setIsSubmitting(true);
        try {
            const copied = await copyProfileFromEmployee({
                employee,
                sourceEmployeeId,
                copyCandidates,
                assignProfile,
                setOverlay,
                queryClient,
                t,
            });
            if (copied) {
                onDone();
            }
        } catch (error) {
            handleError({ error });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (step === 'choice') {
        return (
            <ChoiceStep
                employee={employee}
                copyCandidates={copyCandidates}
                onSelectExisting={() => {
                    setCameFromChoice(true);
                    setStep('selector');
                }}
                onCreateGuided={() => onCreateGuided(employee.id, fullName(employee))}
                onCopyFrom={(sourceId) => void handleCopyFrom(sourceId)}
                onCancel={onCancel}
                canManageCompetencies={canManageCompetencies}
            />
        );
    }

    return (
        <ProfileSelector
            employee={employee}
            initialProfileId={
                isValidProfileId(employee.assignedProfileId)
                    ? employee.assignedProfileId
                    : undefined
            }
            isSubmitting={isSubmitting}
            onConfirm={(result) => void handleConfirm(result)}
            onCreateGuided={() => onCreateGuided(employee.id, fullName(employee))}
            onCancel={cameFromChoice ? () => setStep('choice') : onCancel}
            canManageCompetencies={canManageCompetencies}
        />
    );
};
